import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Send as SendIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import leadSubmissionsService from '../services/leadSubmissionsService';
import serviceProvidersService from '../services/serviceProvidersService';

const OpenedLeadsResubmission = () => {
  const [loading, setLoading] = useState(false);
  const [openedLeads, setOpenedLeads] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [resubmitResult, setResubmitResult] = useState(null);
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await serviceProvidersService.getServiceProviders();
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchOpenedLeads = async () => {
    if (!selectedProvider) return;
    
    setLoading(true);
    try {
      const response = await leadSubmissionsService.getOpenedLeads({
        provider_id: selectedProvider
      });
      setOpenedLeads(response.data || []);
      setSelectedLeads([]); // Clear selection when fetching new data
    } catch (error) {
      console.error('Error fetching opened leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads(openedLeads.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleResubmitLeads = async () => {
    if (selectedLeads.length === 0) return;

    setLoading(true);
    try {
      const response = await leadSubmissionsService.resubmitOpenedLeads(selectedLeads);
      setResubmitResult(response);
      setResubmitDialogOpen(true);
      
      // Refresh the list after resubmission
      fetchOpenedLeads();
    } catch (error) {
      console.error('Error resubmitting leads:', error);
      setResubmitResult({
        success: false,
        error: error.message
      });
      setResubmitDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const getFeedbackStatusColor = (status) => {
    switch (status) {
      case 'qualified': return 'success';
      case 'converted': return 'info';
      case 'rejected':
      case 'invalid': return 'error';
      case 'contacted': return 'info';
      case 'duplicate': return 'warning';
      case 'opened': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          OPENED Leads Resubmission
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchOpenedLeads}
            disabled={!selectedProvider || loading}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SendIcon />}
            onClick={handleResubmitLeads}
            disabled={selectedLeads.length === 0 || loading}
            variant="contained"
            color="primary"
          >
            Resubmit Selected ({selectedLeads.length})
          </Button>
        </Box>
      </Box>

      {/* Provider Selection */}
      <Box mb={3}>
        <FormControl fullWidth>
          <InputLabel>Select Service Provider</InputLabel>
          <Select
            value={selectedProvider}
            onChange={(e) => {
              setSelectedProvider(e.target.value);
              // Auto-fetch when provider is selected
              if (e.target.value) {
                setTimeout(() => fetchOpenedLeads(), 100);
              }
            }}
            label="Select Service Provider"
          >
            {providers.map(provider => (
              <MenuItem key={provider.id} value={provider.id}>
                {provider.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Results Table */}
      {selectedProvider && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedLeads.length > 0 && selectedLeads.length < openedLeads.length}
                    checked={openedLeads.length > 0 && selectedLeads.length === openedLeads.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Reference ID</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Feedback Status</TableCell>
                <TableCell>Received At</TableCell>
                <TableCell>Provider</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {openedLeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">
                      {selectedProvider ? 'No OPENED leads found for the selected provider' : 'Please select a service provider'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                openedLeads.map(lead => (
                  <TableRow key={lead.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {lead.submission_reference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lead.Call?.phone || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {lead.Call?.agent?.username || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {lead.Call?.agent?.id || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.feedback_status}
                        size="small"
                        color={getFeedbackStatusColor(lead.feedback_status)}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(lead.feedback_received_at)}
                    </TableCell>
                    <TableCell>
                      {lead.ServiceProvider?.name || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Resubmit Result Dialog */}
      <Dialog open={resubmitDialogOpen} onClose={() => setResubmitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resubmission Results</DialogTitle>
        <DialogContent>
          {resubmitResult && (
            <Alert 
              severity={resubmitResult.success ? 'success' : 'error'} 
              sx={{ mb: 2 }}
            >
              {resubmitResult.success ? (
                <div>
                  <strong>Resubmission Successful!</strong><br />
                  {resubmitResult.message}<br />
                  Successfully resubmitted: {resubmitResult.data?.filter(r => r.status === 'success').length}<br />
                  Errors: {resubmitResult.data?.filter(r => r.status === 'error').length}
                </div>
              ) : (
                resubmitResult.error
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResubmitDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OpenedLeadsResubmission;